import { DEFAULT_SETTLE_AGREEMENT } from "../../shared/constants/settleAgreement";
import type { CustomerPayload } from "../../services/domains/customer/types";

const DEFAULT_COUNTRY = "Việt Nam";

const getByPath = (source: any, path: string) =>
  path
    .split(".")
    .reduce((value, key) => (value == null ? value : value[key]), source);

const getParentId = (parent: any) => {
  if (typeof parent === "string") return parent || undefined;
  return parent?._id || undefined;
};

export const createCustomerDraft = (temporaryId: string, parent?: string) => ({
  _id: temporaryId,
  status: "new",
  code: null,
  name: null,
  fullName: null,
  phoneNumber: null,
  businessLicense: { text: null },
  settleAgreement: DEFAULT_SETTLE_AGREEMENT,
  address: {
    country: DEFAULT_COUNTRY,
    province: null,
    text: null,
  },
  ...(parent ? { parent } : {}),
});

export const insertCustomerChildDraft = (
  tree: any[],
  parentId: string,
  draft: any,
) => {
  let inserted = false;
  const nextTree = tree.map((customer) => {
    if (String(customer._id) !== String(parentId) || customer.parent) {
      return customer;
    }

    inserted = true;
    return {
      ...customer,
      children: [...(customer.children ?? []), draft],
    };
  });

  return inserted ? nextTree : tree;
};

export const updateCustomerTreeNode = (
  tree: any[],
  targetId: string,
  changes: any,
): any[] =>
  tree.map((customer) => {
    if (String(customer._id) === String(targetId)) {
      const updatedCustomer = { ...customer, ...changes };

      // The edit snapshot can be older than children saved in parallel.
      if (Object.prototype.hasOwnProperty.call(customer, "children")) {
        updatedCustomer.children = customer.children;
      }

      return updatedCustomer;
    }

    if (!customer.children?.length) return customer;
    return {
      ...customer,
      children: updateCustomerTreeNode(customer.children, targetId, changes),
    };
  });

export const removeCustomerTreeNode = (
  tree: any[],
  targetId: string,
): any[] =>
  tree
    .filter((customer) => String(customer._id) !== String(targetId))
    .map((customer) =>
      customer.children?.length
        ? {
            ...customer,
            children: removeCustomerTreeNode(customer.children, targetId),
          }
        : customer,
    );

export const buildCustomerPayload = (customer: any): CustomerPayload => {
  const parent = getParentId(customer.parent);

  return {
    code: customer.code,
    name: customer.name,
    fullName: customer.fullName,
    phoneNumber: customer.phoneNumber || null,
    businessLicense: {
      text: customer.businessLicense?.text,
    },
    settleAgreement:
      customer.settleAgreement ?? DEFAULT_SETTLE_AGREEMENT,
    address: {
      country: customer.address?.country || DEFAULT_COUNTRY,
      province: customer.address?.province,
      text: customer.address?.text,
    },
    ...(parent ? { parent } : {}),
  };
};

const REQUIRED_CUSTOMER_FIELDS = [
  "code",
  "name",
  "fullName",
  "businessLicense.text",
  "address.province",
  "address.text",
];

export const getMissingCustomerRequiredFields = (customer: any) =>
  REQUIRED_CUSTOMER_FIELDS.filter((field) => {
    const value = getByPath(customer, field);
    return value === null || value === undefined || String(value).trim() === "";
  });
